
import Anchor from "../../../../../shared/lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutEvent from "../../../../../shared/lib/html5-software-library/src/ranj/layout/LayoutEvent";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import TutorialPlayer from "../controller/TutorialPlayer";
import GameConfig from "../model/GameConfig";
import GameModel from "../model/GameModel";
import GameStatus from "../model/GameStatus";
import SaveModel from "../model/SaveModel";
import TutorialPlayerEvent from "../model/TutorialPlayerEvent";
import SoundID from "../SoundID";
import AbstractFlexibilityScreenMediator from "./AbstractFlexibilityScreenMediator";
import ConfettiView from "./ConfettiView";
import HudView from "./HudView";
import RoadBuilder from "./RoadBuilder";
import RoadView from "./RoadView";

export default class GameScreenMediator extends AbstractFlexibilityScreenMediator {

    /**
     * @param {*} target 
     * @param {*} dispatcher 
     * @param {*} languagePack 
     * @param {*} layoutManager 
     * @param {*} soundManager 
     * @param {GameModel} gameModel 
     * @param {GameConfig} gameConfig 
     * @param {RoadBuilder} roadBuilder 
     * @param {Object} roadAssets 
     * @param {SaveModel} saveModel 
     * @param {TutorialPlayer} tutorialPlayer 
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, gameConfig, roadBuilder, roadAssets, saveModel, tutorialPlayer) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, saveModel, roadAssets);
        this._gameConfig = gameConfig;
        this._roadBuilder = roadBuilder;
        this._roadAssets = roadAssets;
        this._tutorialPlayer = tutorialPlayer;

        this._handleTickBound = this._handleTick.bind(this);
        this._handleGameModelLaneBound = this._handleGameModelLane.bind(this);
        this._handleGameModelSpeedBound = this._handleGameModelSpeed.bind(this);
        this._handleGameModelOffsetBound = this._handleGameModelOffset.bind(this);
        this._handleGameModelProgressBound = this._handleGameModelProgress.bind(this);
        this._handleGameModelStatusBound = this._handleGameModelStatus.bind(this);
        this._handleGameModelElapsedTimeBound = this._handleGameModelElapsedTime.bind(this);
    }

    initialize() {
        super.initialize();

        this._hud = new HudView(this._target.hud, this._languagePack, this._gameConfig);

        this._road = new RoadView(this._target.road, this._gameModel, this._gameConfig, this._roadAssets);
        this._road.createRoad(this._roadBuilder);

        this._initializeDebug();

        this._road.addEventListener(RoadView.JUMP, this._handleJump.bind(this));
        this._addEventListeners();

        this._confetti = new ConfettiView(this._target.confetti);
        this._confettiAtOffset = this._gameConfig.roadLength - this._gameConfig.getValue("finish.confetti.position");

        this.anchorObject(this._target.confetti, Anchor.T);
        this.anchorObject(this._target.hud, Anchor.T);
        this.anchorObject(this._target.road, Anchor.T);
        this.anchorObject(this._target.bottomBar, Anchor.T);

        this._updateViewportMask();

        const saveGame = this._saveModel.getSaveGame();
        this._reset(saveGame);

        if (!saveGame && getURLParameter("skiptutorial") != "1") {
            this._handleTutorialCompletedBound = this._handleTutorialCompleted.bind(this);
            this.registerEventListener(TutorialPlayerEvent.COMPLETED, this._handleTutorialCompletedBound);

            this._startTutorial();
        } else {
            const delay = this.checkSavegameRestoredFeedback(500) ? 1500 : 0;
            this.callDelayed(this._handleTutorialClose.bind(this), delay);
        }
    }

    _initializeDebug() {
        if (ranj.DEBUG) {
            window.document.addEventListener('keyup', e => {
                const c = String.fromCharCode(e.which);
                if (c == "P") {
                    this._paused = !this._paused;
                    if (!this._paused) {
                        this._tickTime = performance.now();
                    }
                } else if (c == "R") {
                    this._reset();
                    setTimeout(this._startCountdown.bind(this), 500);
                } else if (c == "E" && this._gameModel.status == GameStatus.RUNNING) {
                    this._gameModel.end();
                } else if (c == "F" && this._gameModel.status == GameStatus.RUNNING) {
                    this._gameModel.offset = this._gameConfig.roadLength - 2000;
                } else if ((c >= "1" && c <= "4") && this._gameModel.status == GameStatus.RUNNING) {
                    this._handleJump({ data: parseInt(c - 1) });
                } else if (c == "C") {
                    this._doConfetti();
                } else if (c == "Q") {
                    this._stayOnFastestLane = !this._stayOnFastestLane;
                }
            });
            this._stayOnFastestLane = getURLParameter("fastest") == "1";

            window.gamescreen = this;//for console access
        }
    }

    destroy() {
        super.destroy();
        this._removeEventListeners();
        this._road.destroy();
        this._hud.destroy();
        if (window.gamescreen == this) {
            window.gamescreen = null;
        }
    }

    _addEventListeners() {
        this._gameModel.addEventListener(GameModel.OFFSET, this._handleGameModelOffsetBound);
        this._gameModel.addEventListener(GameModel.SPEED, this._handleGameModelSpeedBound);
        this._gameModel.addEventListener(GameModel.STATUS, this._handleGameModelStatusBound);
        this._gameModel.addEventListener(GameModel.LANE, this._handleGameModelLaneBound);
        this._gameModel.addEventListener(GameModel.PROGRESS, this._handleGameModelProgressBound);
        this._gameModel.addEventListener(GameModel.ELAPSED_TIME, this._handleGameModelElapsedTimeBound);
        this.registerEventListener(LayoutEvent.CHANGED, this._handleLayoutChanged.bind(this));
    }
    _removeEventListeners() {
        this._gameModel.removeEventListener(GameModel.OFFSET, this._handleGameModelOffsetBound);
        this._gameModel.removeEventListener(GameModel.SPEED, this._handleGameModelSpeedBound);
        this._gameModel.removeEventListener(GameModel.STATUS, this._handleGameModelStatusBound);
        this._gameModel.removeEventListener(GameModel.LANE, this._handleGameModelLaneBound);
        this._gameModel.removeEventListener(GameModel.PROGRESS, this._handleGameModelProgressBound);
        this._gameModel.removeEventListener(GameModel.ELAPSED_TIME, this._handleGameModelElapsedTimeBound);
    }

    _handleLayoutChanged() {
        this._updateViewportMask();
    }

    _updateViewportMask() {
        if (getURLParameter("mask") != "0") {
            const vp = this._layoutManager.getViewport(this._target);//in stage coordspace
            this._target.mask = new createjs.Shape();
            this._target.mask.graphics.f("#ff0000").r(vp.x, vp.y, vp.width, vp.height);
        }
    }

    _handleGameModelLane({ data: lane }) {
        this._road.setLane(lane, this._gameModel.status == GameStatus.RUNNING || this._gameModel.status == GameStatus.TUTORIAL);
    }

    _handleGameModelSpeed({ data: speed }) {
        this._hud.setSpeed(speed);
        this._road.setSpeed(speed);
    }

    _handleGameModelOffset({ data: offset }) {
        this._road.setOffset(offset);
        if (this._stayOnFastestLane && this._gameModel.status == GameStatus.RUNNING) {
            this._gameModel.switchToFastestLane();
        }
        if (!this._confettiDone && offset > this._confettiAtOffset) {
            this._doConfetti();
            this._confettiDone = true;
        }
    }

    _handleGameModelProgress({ data: progress }) {
        this._hud.setProgress(progress);
    }

    _handleGameModelStatus({ data: status }) {
        this._road.enableInteraction(status == GameStatus.RUNNING);
        switch (status) {
            case GameStatus.RUNNING:
                this._startTicker();
                break;
            case GameStatus.FINISH:
                this._scheduleFinishPopup();
                break;
            case GameStatus.END:
                this._stopTicker();
                this.gotoScreen(SkinKeys.END_SCREEN_SKIN);
                break;
        }
    }

    _handleGameModelElapsedTime({ data: elapsedTime }) {
        this._hud.setTime(elapsedTime);
    }


    _reset(snapshot) {
        this._stopTicker();
        this._road.setCameraOffset(0);
        if (snapshot && snapshot.status == GameStatus.RUNNING) {
            this._gameModel.applySnapshot(snapshot, true);
        } else {
            this._gameModel.reset();
        }
        this._confettiDone = false;
    }


    _startCountdown() {
        if (getURLParameter("skipcountdown") != "1") {
            const delay = this._gameConfig.getValue("countdown.delay");
            this.callDelayed(() => {
                this.openPopup(SkinKeys.COUNTDOWN_SKIN, this._onCountdownComplete.bind(this));
            }, delay);
        } else {
            this._onCountdownComplete();
        }
    }

    _onCountdownComplete() {
        this._gameModel.start();
    }


    _startTicker() {
        this._tickTime = performance.now();
        this._target.stage.addEventListener("tick", this._handleTickBound);
    }
    _stopTicker() {
        this._target.stage.removeEventListener("tick", this._handleTickBound);
    }
    _handleTick() {
        if (!this._paused) {
            const now = performance.now();
            const delta = now - this._tickTime;
            this._tickTime = now;
            this._gameModel.step(delta);
            switch (this._gameModel.status) {
                case GameStatus.FINISH:
                    if (this._gameModel.speed > 0) {
                        this._road.moveCamera(this._gameConfig.getValue("finish.cameraOffset"));
                    }
                    break;
            }
        }
    }


    _handleJump({ data: goal }) {
        const lane = this._gameModel.lane;
        this._gameModel.lane = goal;
        if (lane != goal) {
            //this.playSound(SoundID.LANESWITCH);
        }
    }


    _startTutorial() {
        this._tutorialPlayer.initialize();
        this._tutorialPlayer.roadView = this._road;
        this._tutorialPlayer.hudView = this._hud;

        this._handleTutorialReplayBound = this._handleTutorialReplay.bind(this);
        this.registerEventListener(TutorialPlayerEvent.REPLAY, this._handleTutorialReplayBound);

        this._tutorialPlayer.start();
        this._startTicker();
    }

    // TODO: to be removed?
    _handleTutorialClose() {
        this.callDelayed(this._startCountdown.bind(this), 1000);
    }

    _handleTutorialCompleted() {
        this.unregisterEventListener(TutorialPlayerEvent.COMPLETED, this._handleTutorialCompletedBound);
        this._stopTicker();

        this.callDelayed(this._startCountdown.bind(this), 1000);
    }

    _handleTutorialReplay() {
        this.unregisterEventListener(TutorialPlayerEvent.REPLAY, this._handleTutorialReplayBound);

        this._tutorialPlayer.reset();

        this._startTutorial();
    }

    _scheduleFinishPopup() {
        const delay = this._gameConfig.getValue("finish.delayPopup");
        this.callDelayed(this._doFinishPopup.bind(this), delay);
    }

    _doFinishPopup() {
        this.openPopup(SkinKeys.FINISH_SKIN);
    }

    _doConfetti() {
        const cfg = this._gameConfig.getValue("finish.confetti");
        this._confetti.animate(cfg.offset, cfg.duration);
    }
}
